import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  collection, getDocs, addDoc, serverTimestamp,
  doc, updateDoc
} from 'firebase/firestore';
import { getFirestore, query, orderBy, getDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth } from '../../helpers/firebaseConfig';
import { useAuth } from '../../auth/AuthProvider';
import './AnalysisSidebar.css';

const db = getFirestore();

export default function AnalysisSidebar({
  currentChatId,
  setCurrentChatId,
  isOpen,
  chatPathIds,          // <-- from ModelUI
  setChatPathIds        // <-- from ModelUI
}) {
  const { user } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [analysisCache, setAnalysisCache] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [crumbTitles, setCrumbTitles] = useState([]);
  const [creating, setCreating] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState('');
  const identity = user?.displayName || user?.email || 'Signed-in user';

  const handleSignOut = async () => {
    setIsSigningOut(true);
    setSignOutError('');
    try {
      await signOut(auth);
    } catch (error) {
      setSignOutError(String(error?.message || 'Could not log out.'));
      setIsSigningOut(false);
    }
  };

  // case-insensitive/natural-ish title sort
  const sortAnalyses = (arr) =>
    [...arr].sort((a, b) => {
      const aIsFolder = a.type === 'folder' ? 0 : 1;
      const bIsFolder = b.type === 'folder' ? 0 : 1;
      if (aIsFolder !== bIsFolder) return aIsFolder - bIsFolder; // folders on top
      return (a.title || '').localeCompare(b.title || '', undefined, {
        sensitivity: 'base',
        numeric: true
      });
    });

  // resolve the collection being shown from the path (root or inside a folder)
  const currentCollectionRef = useMemo(() => {
    let col = collection(db, 'Analyses');
    for (const folderId of chatPathIds || []) {
      const parent = doc(col, folderId);
      col = collection(parent, 'Analyses');
    }
    return col;
  }, [chatPathIds]);

  const pathKey = useMemo(() => (chatPathIds || []).join('>'), [chatPathIds]);
  const cachedAnalyses = analysisCache[pathKey];


    // load breadcrumb titles for folders in the current path
  useEffect(() => {
    (async () => {
      const titles = [];
      let col = collection(db, 'Analyses');
      for (const folderId of chatPathIds || []) {
        const dref = doc(col, folderId);
        const snap = await getDoc(dref);
        titles.push((snap.data()?.title) || 'Folder');
        col = collection(dref, 'Analyses');
      }
      setCrumbTitles(titles);
    })();
  }, [chatPathIds]);

  const fetchAnalyses = useCallback(async (force = false) => {
    if (!isOpen && !force) return;
    if (!force && cachedAnalyses) return;
    setIsLoading(true);
    const q = query(currentCollectionRef, orderBy('title', 'asc'));
    try {
      const snap = await getDocs(q);
      const list = snap.docs.map(d => {
        const data = d.data() || {};
        return { id: d.id, title: data.title, type: data.type || 'chat' };
      });
      const sorted = sortAnalyses(list);
      setAnalyses(sorted);
      setAnalysisCache(prev => ({ ...prev, [pathKey]: sorted }));
    } finally {
      setIsLoading(false);
    }
  }, [cachedAnalyses, currentCollectionRef, isOpen, pathKey]);

  useEffect(() => {
    if (cachedAnalyses) {
      setAnalyses(cachedAnalyses);
      setIsLoading(false);
      return;
    }
    if (isOpen) {
      fetchAnalyses();
    }
  }, [cachedAnalyses, fetchAnalyses, isOpen]);

  useEffect(() => {
    if (isOpen && !cachedAnalyses) {
      fetchAnalyses();
    }
  }, [isOpen, cachedAnalyses, fetchAnalyses]);

  const handleNewChat = async () => {
    if (creating) return;
    setCreating(true);
    const now = new Date().toISOString().replace('T', ' ').slice(0, 19);
    const ref = await addDoc(currentCollectionRef, {
      title: `Chat at ${now}`,
      type: 'chat',
      createdAt: serverTimestamp()
    });
    await fetchAnalyses(true);
    setCurrentChatId(ref.id);
    setCreating(false);
  };

  const handleNewFolder = async () => {
    if (creating) return;
    setCreating(true);
    await addDoc(currentCollectionRef, {
      title: 'New folder',
      name: 'New folder',
      type: 'folder',
      createdAt: serverTimestamp()
    });
    await fetchAnalyses(true);
    setCreating(false);
  };

  const openMenu = (id) => setMenuOpenId(id);
  const closeMenu = () => setMenuOpenId(null);

  const startRename = (a) => {
    setEditingId(a.id);
    setDraftTitle(a.title || '');
    closeMenu();
  };

  const saveTitle = async (id, newTitle) => {
    const t = (newTitle || '').trim() || 'Untitled';
    await updateDoc(doc(currentCollectionRef, id), { title: t, name: t });
    setAnalyses(prev => {
      const updated = sortAnalyses(prev.map(p => (p.id === id ? { ...p, title: t } : p)));
      setAnalysisCache(cache => ({ ...cache, [pathKey]: updated }));
      return updated;
    });
    setEditingId(null);
  };

  const sidebarClass = isOpen ? 'sidebar' : 'sidebar closed';

  const breadcrumb = ['', ...crumbTitles];

  const enterFolder = (folder) => {
    setChatPathIds([...(chatPathIds || []), folder.id]);
    // do not set a chat here; the list view will now show the folder’s children
  };

  const goBack = () => {
    if (!(chatPathIds && chatPathIds.length)) return;
    setChatPathIds(chatPathIds.slice(0, -1));
  };

  return (
    <div className={sidebarClass}>
      <div style={{ width: 270, background: '#0f0f0f', color: '#fff', padding: 12,
                    borderRight: '1px solid #333', display: 'flex', flexDirection: 'column',
                    height: '100vh', boxSizing: 'border-box' }}>

        <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 14, paddingLeft: 38 }}>
          WUI-Planner
        </div>

        {/* breadcrumbs + back */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          {(chatPathIds && chatPathIds.length > 0) && (
            <button onClick={goBack}
              style={{ background: 'transparent', border: '1px solid #444', color: '#fff',
                       borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>
              ← Back
            </button>
          )}
          <div style={{ fontWeight: 700, opacity: 0.9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {breadcrumb.join(' / ')}
          </div>
        </div>

        {/* create buttons */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button onClick={handleNewChat} disabled={creating}
            style={{ background: '#003566', color: '#fff', border: 'none', borderRadius: 6,
                     padding: '8px 10px', cursor: 'pointer', fontWeight: 600, flex: 1 }}>
            {creating ? 'Working…' : '＋ Chat'}
          </button>
          <button onClick={handleNewFolder} disabled={creating}
            style={{ background: '#1a3a5c', color: '#fff', border: 'none', borderRadius: 6,
                     padding: '8px 10px', cursor: 'pointer', fontWeight: 600 }}>
            📁 Folder
          </button>
        </div>

        {/* list */}
        <div style={{ overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
          {isLoading && !analyses.length && (
            <div style={{ padding: 12, color: '#aaa' }}>Loading analyses…</div>
          )}
          {analyses.map(a => {
            const isActive = a.type === 'chat' && a.id === currentChatId;
            const isMenu = menuOpenId === a.id;
            return (
              <div
                key={a.id}
                className={`chat-row${isMenu ? ' menu-open' : ''}`}
                onClick={() => (a.type === 'folder' ? enterFolder(a) : setCurrentChatId(a.id))}
                onMouseLeave={closeMenu}
                style={{
                  position: 'relative', padding: '8px 15px 8px 6px', marginBottom: 4,
                  borderRadius: 4, cursor: 'pointer',
                  background: isActive ? '#1e1e1e' : 'transparent',
                  fontWeight: isActive ? 600 : 400,
                  whiteSpace: 'normal', wordBreak: 'break-word',
                  display: 'flex', alignItems: 'center', gap: 8
                }}
              >
                <span aria-hidden style={{ opacity: 0.9 }}>
                  {a.type === 'folder' ? '📁' : ''}
                </span>

                {editingId === a.id ? (
                  <input
                    autoFocus value={draftTitle} onChange={e => setDraftTitle(e.target.value)}
                    onBlur={() => saveTitle(a.id, draftTitle)}
                    onKeyDown={e => { if (e.key === 'Enter') saveTitle(a.id, draftTitle); if (e.key === 'Escape') setEditingId(null); }}
                    style={{ width: '100%', background: 'transparent', border: '1px solid #444',
                             borderRadius: 4, color: '#fff', padding: '2px 4px' }}
                  />
                ) : (
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {a.title || a.id}
                  </span>
                )}

                {!editingId && (
                  <span onClick={(e) => { e.stopPropagation(); openMenu(a.id); }}
                        style={{ position: 'absolute', right: 0, top: 6, cursor: 'pointer' }}
                        className="row-more">⋯</span>
                )}

                {menuOpenId === a.id && (
                  <div
                    style={{ position: 'absolute', right: 24, top: 4, background: '#222',
                             border: '1px solid #444', borderRadius: 4, zIndex: 10 }}
                    onClick={e => e.stopPropagation()}
                  >
                    <div onClick={() => startRename(a)} style={{ padding: '6px 12px', cursor: 'pointer' }}>
                      Rename
                    </div>
                    {a.type === 'folder' && (
                      <div onClick={() => { setMenuOpenId(null); enterFolder(a); }}
                           style={{ padding: '6px 12px', cursor: 'pointer' }}>
                        Open
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ borderTop: '1px solid #333', paddingTop: 12, marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
            {user?.photoURL ? (
              <img
                src={user.photoURL}
                alt=""
                referrerPolicy="no-referrer"
                style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', flex: '0 0 34px' }}
              />
            ) : (
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#2563eb', display: 'grid', placeItems: 'center', fontWeight: 800, flex: '0 0 34px' }}>
                {identity.charAt(0).toUpperCase()}
              </div>
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {identity}
              </div>
              {user?.displayName && user?.email ? (
                <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.email}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={handleSignOut}
              disabled={isSigningOut}
              style={{ padding: '6px 8px', border: '1px solid #475569', borderRadius: 6, background: '#1e293b', color: '#fff', cursor: isSigningOut ? 'wait' : 'pointer', fontSize: 12 }}
            >
              {isSigningOut ? '…' : 'Log out'}
            </button>
          </div>
          {signOutError ? <div style={{ color: '#fecaca', fontSize: 11, marginTop: 7 }}>{signOutError}</div> : null}
        </div>
      </div>
    </div>
  );
}
